import type { BenefitCategoryCode, DeductionCategoryCode } from './categories';
import type {
  ApprovalStatus, ArrearsBehavior, BenefitCatalogItem, BenefitElection, BenefitOverview, BenefitPlan, BenefitPlanInput, BulkAssignmentPreview,
  BulkAssignmentResult, CatchUpBehavior, ConfigurationStatus, ContributionMethod, CoverageLevel, CoverageTier, CreatesPayrollDeduction,
  DeductionCalculationType, DeductionCatalogItem, DeductionClassification, DeductionDefinition, DeductionDefinitionInput, DeductionElection,
  DeductionSummary, EnrollmentMethod, ExcludedReason, FundingModel, GarnishmentRecord, LimitRule, Paged, PayrollFrequency, StopRule, TaxProfile,
  TaxTreatmentCell, UpcomingKind,
  CatalogItemType, CatalogSourceState, CompanyCatalogItem, CompanyCatalogPage, DeclaredTaxCell, DeclaredTaxProfile, DeclaredTaxProfileInput, DeclaredTaxTreatment,
} from './types';

/**
 * Wire <-> UI mapping for the Http gateways. The API sends camelCase JSON (contract section 4); these functions read it defensively (a missing
 * optional field becomes null, a missing list becomes an empty list) so a backend that omits a null never breaks a screen, and they never trust a
 * value's type without checking it. No response body is ever logged.
 */

type Raw = Record<string, unknown>;

function obj(value: unknown): Raw {
  return typeof value === 'object' && value !== null ? (value as Raw) : {};
}
function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}
function strOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}
function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && !Number.isNaN(value) ? value : fallback;
}
function numOrNull(value: unknown): number | null {
  return typeof value === 'number' && !Number.isNaN(value) ? value : null;
}
function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}
function boolOrNull(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}
function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}
function strings(value: unknown): string[] {
  return arr(value).filter((v): v is string => typeof v === 'string');
}
/** An enum value sent as a string, or the fallback when the server sent something else. */
function pick<T extends string>(value: unknown, fallback: T): T {
  return typeof value === 'string' && value !== '' ? (value as T) : fallback;
}
function pickOrNull<T extends string>(value: unknown): T | null {
  return typeof value === 'string' && value !== '' ? (value as T) : null;
}

/** `?a=1&b=two`, skipping undefined, null and empty values. Never put personal data in a query string: callers pass ids, filters and paging only. */
export function buildQuery(params: Record<string, string | number | boolean | null | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

export function mapPaged<T>(raw: unknown, mapItem: (item: unknown) => T): Paged<T> {
  const r = obj(raw);
  const items = arr(r.items).map(mapItem);
  return { items, page: num(r.page, 1), pageSize: num(r.pageSize, items.length || 25), totalCount: num(r.totalCount, items.length) };
}

/** Some list endpoints may return a bare array; accept either. */
export function mapList<T>(raw: unknown, mapItem: (item: unknown) => T): T[] {
  if (Array.isArray(raw)) return raw.map(mapItem);
  return arr(obj(raw).items).map(mapItem);
}

// ---- benefits ----

export function mapBenefitCatalogItem(raw: unknown): BenefitCatalogItem {
  const r = obj(raw);
  return {
    code: str(r.code),
    name: str(r.name),
    categoryCode: str(r.categoryCode) as BenefitCategoryCode,
    description: str(r.description),
    createsPayrollDeduction: pick<CreatesPayrollDeduction>(r.createsPayrollDeduction, 'Never'),
    leaveLinked: bool(r.leaveLinked),
    leaveNote: strOrNull(r.leaveNote),
    version: num(r.version, 1),
    sortOrder: num(r.sortOrder),
    isHidden: bool(r.isHidden),
  };
}

function mapTier(raw: unknown): CoverageTier {
  const r = obj(raw);
  return { coverageLevel: pick<CoverageLevel>(r.coverageLevel, 'EmployeeOnly'), employeeAmount: numOrNull(r.employeeAmount), employerAmount: numOrNull(r.employerAmount) };
}

export function mapBenefitPlan(raw: unknown): BenefitPlan {
  const r = obj(raw);
  return {
    id: str(r.id),
    name: str(r.name),
    shortCode: str(r.shortCode),
    categoryCode: str(r.categoryCode) as BenefitCategoryCode,
    typeCode: strOrNull(r.typeCode),
    isCustom: bool(r.isCustom, strOrNull(r.typeCode) === null),
    description: strOrNull(r.description),
    providerName: strOrNull(r.providerName),
    planYear: numOrNull(r.planYear),
    effectiveStartDate: strOrNull(r.effectiveStartDate),
    effectiveEndDate: strOrNull(r.effectiveEndDate),
    eligibilityDescription: strOrNull(r.eligibilityDescription),
    waitingPeriodDays: numOrNull(r.waitingPeriodDays),
    enrollmentMethod: pick<EnrollmentMethod>(r.enrollmentMethod, 'NotApplicable'),
    fundingModel: pick<FundingModel>(r.fundingModel, 'NotApplicable'),
    contributionMethod: pick<ContributionMethod>(r.contributionMethod, 'NoPayrollContribution'),
    employeeContributionAmount: numOrNull(r.employeeContributionAmount),
    employerContributionAmount: numOrNull(r.employerContributionAmount),
    employeeContributionPercent: numOrNull(r.employeeContributionPercent),
    employerContributionPercent: numOrNull(r.employerContributionPercent),
    coverageTiers: arr(r.coverageTiers).map(mapTier),
    payrollFrequency: pick<PayrollFrequency>(r.payrollFrequency, 'EveryPayroll'),
    associatedDeductionCodeId: strOrNull(r.associatedDeductionCodeId),
    associatedEmployerContributionCodeId: strOrNull(r.associatedEmployerContributionCodeId),
    taxProfileCode: strOrNull(r.taxProfileCode),
    w2ReportingCode: strOrNull(r.w2ReportingCode),
    planDocumentReference: strOrNull(r.planDocumentReference),
    employeeVisibleDescription: strOrNull(r.employeeVisibleDescription),
    internalNotes: strOrNull(r.internalNotes),
    isActive: bool(r.isActive, true),
    declaredTaxProfile: mapDeclaredTaxProfile(r.declaredTaxProfile),
    configurationStatus: pick<ConfigurationStatus>(r.configurationStatus, 'Complete'),
    configurationIssues: strings(r.configurationIssues),
    createsPayrollDeduction: bool(r.createsPayrollDeduction),
    leaveLinked: bool(r.leaveLinked),
    leaveNote: strOrNull(r.leaveNote),
    enrolledCount: num(r.enrolledCount),
    concurrencyVersion: num(r.concurrencyVersion, 1),
    createdAtUtc: str(r.createdAtUtc),
    updatedAtUtc: strOrNull(r.updatedAtUtc),
  };
}

/** The write body is exactly the writable subset of the contract (4.1.1): no id, status, counts or timestamps. */
export function benefitPlanBody(input: BenefitPlanInput): BenefitPlanInput {
  return {
    name: input.name.trim(),
    shortCode: input.shortCode.trim(),
    categoryCode: input.categoryCode,
    typeCode: input.typeCode,
    description: input.description,
    providerName: input.providerName,
    planYear: input.planYear,
    effectiveStartDate: input.effectiveStartDate,
    effectiveEndDate: input.effectiveEndDate,
    eligibilityDescription: input.eligibilityDescription,
    waitingPeriodDays: input.waitingPeriodDays,
    enrollmentMethod: input.enrollmentMethod,
    fundingModel: input.fundingModel,
    contributionMethod: input.contributionMethod,
    employeeContributionAmount: input.employeeContributionAmount,
    employerContributionAmount: input.employerContributionAmount,
    employeeContributionPercent: input.employeeContributionPercent,
    employerContributionPercent: input.employerContributionPercent,
    coverageTiers: input.coverageTiers.map((t) => ({ coverageLevel: t.coverageLevel, employeeAmount: t.employeeAmount, employerAmount: t.employerAmount })),
    payrollFrequency: input.payrollFrequency,
    associatedDeductionCodeId: input.associatedDeductionCodeId,
    associatedEmployerContributionCodeId: input.associatedEmployerContributionCodeId,
    taxProfileCode: input.taxProfileCode,
    w2ReportingCode: input.w2ReportingCode,
    planDocumentReference: input.planDocumentReference,
    employeeVisibleDescription: input.employeeVisibleDescription,
    internalNotes: input.internalNotes,
    isActive: input.isActive,
    ...declaredTaxProfileBody(input.declaredTaxProfile),
  };
}

export function mapBenefitOverview(raw: unknown): BenefitOverview {
  const r = obj(raw);
  return {
    asOf: str(r.asOf),
    totalActivePlans: num(r.totalActivePlans),
    plansAwaitingConfiguration: num(r.plansAwaitingConfiguration),
    plansApproachingExpiration: arr(r.plansApproachingExpiration).map((p) => ({ id: str(obj(p).id), name: str(obj(p).name), effectiveEndDate: str(obj(p).effectiveEndDate) })),
    enrolledEmployees: num(r.enrolledEmployees),
    waivedEmployees: num(r.waivedEmployees),
    estimatedEmployeeContributionPerPayroll: num(r.estimatedEmployeeContributionPerPayroll),
    estimatedEmployerContributionPerPayroll: num(r.estimatedEmployerContributionPerPayroll),
    contributionNote: str(r.contributionNote),
    upcoming: arr(r.upcoming).map((u) => ({ kind: pick<UpcomingKind>(obj(u).kind, 'PlanStart'), date: str(obj(u).date), name: str(obj(u).name) })),
    categoryBreakdown: arr(r.categoryBreakdown).map((c) => ({ categoryCode: str(obj(c).categoryCode), activePlans: num(obj(c).activePlans), enrolled: num(obj(c).enrolled) })),
    recentChanges: arr(r.recentChanges).map((c) => ({ eventType: str(obj(c).eventType), occurredAtUtc: str(obj(c).occurredAtUtc), summary: str(obj(c).summary) })),
  };
}

export function mapBenefitElection(raw: unknown): BenefitElection {
  const r = obj(raw);
  return {
    id: str(r.id),
    employeeId: str(r.employeeId),
    benefitCodeId: str(r.benefitCodeId, str(r.planId)),
    employeeContributionPercentOverride: numOrNull(r.employeeContributionPercentOverride),
    companyContributionPercentOverride: numOrNull(r.companyContributionPercentOverride),
    amountOverride: numOrNull(r.amountOverride),
    effectiveStartDate: str(r.effectiveStartDate),
    effectiveEndDate: strOrNull(r.effectiveEndDate),
    coverageLevel: pickOrNull<CoverageLevel>(r.coverageLevel),
    isWaived: bool(r.isWaived),
    qualifyingEventReason: strOrNull(r.qualifyingEventReason),
    enrollmentSource: pickOrNull<EnrollmentMethod>(r.enrollmentSource),
    planName: strOrNull(r.planName),
    planCategoryCode: strOrNull(r.planCategoryCode),
    concurrencyVersion: num(r.concurrencyVersion, 1),
    createdAtUtc: str(r.createdAtUtc),
    updatedAtUtc: strOrNull(r.updatedAtUtc),
  };
}

// ---- deductions ----

export function mapDeductionCatalogItem(raw: unknown): DeductionCatalogItem {
  const r = obj(raw);
  return {
    code: str(r.code),
    name: str(r.name),
    categoryCode: str(r.categoryCode) as DeductionCategoryCode,
    categoryName: str(r.categoryName),
    description: str(r.description),
    classification: pick<DeductionClassification>(r.classification, 'Voluntary'),
    defaultCalculationType: pickOrNull<DeductionCalculationType>(r.defaultCalculationType),
    isStatutory: bool(r.isStatutory, str(r.classification) === 'Statutory'),
    // A statutory row is always read only, even if an older server omits the flag.
    isReadOnly: bool(r.isReadOnly, bool(r.isStatutory, str(r.classification) === 'Statutory')),
    requiresRuleReview: bool(r.requiresRuleReview, str(r.categoryCode) === 'garnishments-levies'),
    version: num(r.version, 1),
    sortOrder: num(r.sortOrder),
    isHidden: bool(r.isHidden),
  };
}

export function mapDeductionDefinition(raw: unknown): DeductionDefinition {
  const r = obj(raw);
  const category = strOrNull(r.categoryCode);
  return {
    id: str(r.id),
    name: str(r.name),
    shortCode: strOrNull(r.shortCode),
    categoryCode: category as DeductionCategoryCode | null,
    typeCode: strOrNull(r.typeCode),
    description: strOrNull(r.description),
    classification: pickOrNull<DeductionClassification>(r.classification),
    calculationType: pickOrNull<DeductionCalculationType>(r.calculationType),
    frequency: pickOrNull<PayrollFrequency>(r.frequency),
    taxType: num(r.taxType, 2),
    amountType: num(r.amountType, 1),
    defaultAmount: numOrNull(r.defaultAmount),
    defaultCompanyContribution: numOrNull(r.defaultCompanyContribution),
    w2Code: strOrNull(r.w2Code),
    exemptFederal: boolOrNull(r.exemptFederal),
    exemptFica: boolOrNull(r.exemptFica),
    exemptState: boolOrNull(r.exemptState),
    glAccountDefault: strOrNull(r.glAccountDefault),
    effectiveStartDate: strOrNull(r.effectiveStartDate),
    effectiveEndDate: strOrNull(r.effectiveEndDate),
    annualLimit: numOrNull(r.annualLimit),
    perPayPeriodLimit: numOrNull(r.perPayPeriodLimit),
    goalBalance: numOrNull(r.goalBalance),
    priority: numOrNull(r.priority),
    arrearsBehavior: pickOrNull<ArrearsBehavior>(r.arrearsBehavior),
    catchUpBehavior: pickOrNull<CatchUpBehavior>(r.catchUpBehavior),
    stopRule: pickOrNull<StopRule>(r.stopRule),
    associatedBenefitCodeId: strOrNull(r.associatedBenefitCodeId),
    requiresEmployeeAuthorization: boolOrNull(r.requiresEmployeeAuthorization),
    remittanceRecipientName: strOrNull(r.remittanceRecipientName),
    employeeVisibleLabel: strOrNull(r.employeeVisibleLabel),
    internalNotes: strOrNull(r.internalNotes),
    taxProfileCode: strOrNull(r.taxProfileCode),
    limitRuleCode: strOrNull(r.limitRuleCode),
    isActive: bool(r.isActive, true),
    isCustom: bool(r.isCustom, true),
    declaredTaxProfile: mapDeclaredTaxProfile(r.declaredTaxProfile),
    configurationStatus: pickOrNull<ConfigurationStatus>(r.configurationStatus),
    configurationIssues: strings(r.configurationIssues),
    concurrencyVersion: num(r.concurrencyVersion, 1),
    isSystemDefinition: bool(r.isSystemDefinition),
    createdAtUtc: str(r.createdAtUtc),
    updatedAtUtc: strOrNull(r.updatedAtUtc),
  };
}

/** The body for `POST /api/deduction-codes` and `PUT /api/deduction-codes/{id}`. The legacy fields (`taxType`, `amountType`) stay valid for the
 * existing validators: FixedAmount is a flat amount, every percent type is a percentage, and a new-flow definition is post-tax (the server writes
 * the stored tax type itself and never trusts this one to widen anything). */
export function deductionDefinitionBody(input: DeductionDefinitionInput, existing?: DeductionDefinition): Record<string, unknown> {
  return {
    name: input.name.trim(),
    // A declared Pre-tax treatment sends 1 so the legacy tax type agrees with it (the server refuses a mismatch); otherwise a new-flow definition is post-tax.
    taxType: input.declaredTaxProfile?.treatment === 'PreTax' ? 1 : 2,
    amountType: input.calculationType === 'FixedAmount' ? 1 : 2,
    defaultAmount: input.defaultAmount,
    defaultCompanyContribution: input.defaultCompanyContribution,
    w2Code: existing?.w2Code ?? null,
    exemptFederal: existing?.exemptFederal ?? null,
    exemptFica: existing?.exemptFica ?? null,
    exemptState: existing?.exemptState ?? null,
    glAccountDefault: existing?.glAccountDefault ?? null,
    isActive: input.isActive,
    categoryCode: input.categoryCode,
    shortCode: input.shortCode.trim(),
    description: input.description,
    classification: input.classification,
    calculationType: input.calculationType,
    frequency: input.frequency,
    effectiveStartDate: input.effectiveStartDate,
    effectiveEndDate: input.effectiveEndDate,
    annualLimit: input.annualLimit,
    perPayPeriodLimit: input.perPayPeriodLimit,
    goalBalance: input.goalBalance,
    priority: input.priority,
    arrearsBehavior: input.arrearsBehavior,
    catchUpBehavior: input.catchUpBehavior,
    stopRule: input.stopRule,
    associatedBenefitCodeId: input.associatedBenefitCodeId,
    requiresEmployeeAuthorization: input.requiresEmployeeAuthorization,
    remittanceRecipientName: input.remittanceRecipientName,
    employeeVisibleLabel: input.employeeVisibleLabel,
    internalNotes: input.internalNotes,
    typeCode: input.typeCode,
    taxProfileCode: input.taxProfileCode,
    limitRuleCode: input.limitRuleCode,
    ...declaredTaxProfileBody(input.declaredTaxProfile),
  };
}

// ---- the declared tax profile ----

const cellOrNull = (value: unknown): DeclaredTaxCell | null => (value === 'Exempt' || value === 'Taxable' ? value : null);

/** The server's `declaredTaxProfile` (null when none was declared). The declaring user is never part of it. */
export function mapDeclaredTaxProfile(raw: unknown): DeclaredTaxProfile | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = obj(raw);
  const treatment = r.treatment;
  if (treatment !== 'PreTax' && treatment !== 'PostTax') return null;
  const status = r.status;
  return {
    treatment: treatment as DeclaredTaxTreatment,
    federalIncomeTax: cellOrNull(r.federalIncomeTax),
    socialSecurity: cellOrNull(r.socialSecurity),
    medicare: cellOrNull(r.medicare),
    stateIncomeTax: cellOrNull(r.stateIncomeTax),
    localIncomeTax: cellOrNull(r.localIncomeTax),
    status: status === 'Approved' || status === 'Rejected' ? status : 'NeedsReview',
    declaredAtUtc: strOrNull(r.declaredAtUtc),
  };
}

/** The body fragment for a declared profile: present only when one was declared, and only the writable fields (never a status or a time). */
export function declaredTaxProfileBody(profile: DeclaredTaxProfileInput | null | undefined): { declaredTaxProfile?: DeclaredTaxProfileInput } {
  if (!profile) return {};
  return {
    declaredTaxProfile: {
      treatment: profile.treatment,
      federalIncomeTax: profile.federalIncomeTax,
      socialSecurity: profile.socialSecurity,
      medicare: profile.medicare,
      stateIncomeTax: profile.stateIncomeTax,
      localIncomeTax: profile.localIncomeTax,
    },
  };
}

/** A legacy row (the server reports no category) is updated with the legacy shape and NONE of the new fields: sending any one of them would turn it into a
 * new-flow definition that must then carry a short code and category. Activating or deactivating a legacy row changes `isActive` and nothing else. */
export function legacyDefinitionBody(def: DeductionDefinition, isActive: boolean): Record<string, unknown> {
  return {
    name: def.name,
    taxType: def.taxType,
    amountType: def.amountType,
    defaultAmount: def.defaultAmount,
    defaultCompanyContribution: def.defaultCompanyContribution,
    w2Code: def.w2Code,
    exemptFederal: def.exemptFederal,
    exemptFica: def.exemptFica,
    exemptState: def.exemptState,
    glAccountDefault: def.glAccountDefault,
    isActive,
    expectedVersion: def.concurrencyVersion,
  };
}

/** The writable view of an existing definition, so an activate or deactivate call can send the whole body back unchanged apart from `isActive`. */
export function definitionToInput(def: DeductionDefinition, isActive: boolean): DeductionDefinitionInput {
  return {
    name: def.name,
    shortCode: def.shortCode ?? '',
    categoryCode: (def.categoryCode ?? 'other-custom') as DeductionCategoryCode,
    typeCode: def.typeCode,
    description: def.description,
    classification: def.classification ?? 'Voluntary',
    calculationType: def.calculationType ?? (def.amountType === 2 ? 'PercentOfEligibleGross' : 'FixedAmount'),
    frequency: def.frequency ?? 'EveryPayroll',
    defaultAmount: def.defaultAmount,
    defaultCompanyContribution: def.defaultCompanyContribution,
    effectiveStartDate: def.effectiveStartDate,
    effectiveEndDate: def.effectiveEndDate,
    annualLimit: def.annualLimit,
    perPayPeriodLimit: def.perPayPeriodLimit,
    goalBalance: def.goalBalance,
    priority: def.priority,
    arrearsBehavior: def.arrearsBehavior ?? 'None',
    catchUpBehavior: def.catchUpBehavior ?? 'None',
    stopRule: def.stopRule ?? 'None',
    associatedBenefitCodeId: def.associatedBenefitCodeId,
    requiresEmployeeAuthorization: def.requiresEmployeeAuthorization ?? false,
    remittanceRecipientName: def.remittanceRecipientName,
    employeeVisibleLabel: def.employeeVisibleLabel,
    internalNotes: def.internalNotes,
    taxProfileCode: def.taxProfileCode,
    limitRuleCode: def.limitRuleCode,
    isActive,
  };
}

export function mapDeductionSummary(raw: unknown): DeductionSummary {
  const r = obj(raw);
  return {
    totalActive: num(r.totalActive),
    awaitingConfiguration: num(r.awaitingConfiguration),
    endingSoon: arr(r.endingSoon).map((p) => ({ id: str(obj(p).id), name: str(obj(p).name), effectiveEndDate: str(obj(p).effectiveEndDate) })),
    byCategory: arr(r.byCategory).map((c) => ({ categoryCode: str(obj(c).categoryCode), active: num(obj(c).active), total: num(obj(c).total) })),
    recentChanges: arr(r.recentChanges).map((c) => ({ eventType: str(obj(c).eventType), occurredAtUtc: str(obj(c).occurredAtUtc), summary: str(obj(c).summary) })),
  };
}

export function mapTaxProfile(raw: unknown): TaxProfile {
  const r = obj(raw);
  const treatments: Record<string, TaxTreatmentCell> = {};
  for (const [key, value] of Object.entries(obj(r.treatments))) treatments[key] = pick<TaxTreatmentCell>(value, 'ReviewRequired');
  const status = pick<ApprovalStatus>(r.status, 'Draft');
  return {
    code: str(r.code),
    name: str(r.name),
    description: strOrNull(r.description),
    version: num(r.version, 1),
    effectiveFrom: strOrNull(r.effectiveFrom),
    effectiveTo: strOrNull(r.effectiveTo),
    status,
    // The API sends this; when absent, "approved for payroll" is never assumed from the status alone.
    approvedForPayroll: bool(r.approvedForPayroll, false),
    w2Box: strOrNull(r.w2Box),
    w2Code: strOrNull(r.w2Code),
    sourceTitle: strOrNull(r.sourceTitle),
    sourceUrl: strOrNull(r.sourceUrl),
    isSynthetic: bool(r.isSynthetic),
    treatments,
  };
}

export function mapLimitRule(raw: unknown): LimitRule {
  const r = obj(raw);
  return {
    code: str(r.code),
    name: str(r.name),
    version: num(r.version, 1),
    effectiveFrom: strOrNull(r.effectiveFrom),
    effectiveTo: strOrNull(r.effectiveTo),
    amount: numOrNull(r.amount),
    unit: str(r.unit),
    ageThreshold: numOrNull(r.ageThreshold),
    status: pick<ApprovalStatus>(r.status, 'Draft'),
    isSynthetic: bool(r.isSynthetic),
  };
}

export function mapDeductionElection(raw: unknown): DeductionElection {
  const r = obj(raw);
  return {
    id: str(r.id),
    employeeId: str(r.employeeId),
    deductionCodeId: str(r.deductionCodeId),
    amountOverride: numOrNull(r.amountOverride),
    companyContributionOverride: numOrNull(r.companyContributionOverride),
    title: strOrNull(r.title),
    effectiveStartDate: str(r.effectiveStartDate),
    effectiveEndDate: strOrNull(r.effectiveEndDate),
    createdAtUtc: str(r.createdAtUtc),
    updatedAtUtc: strOrNull(r.updatedAtUtc),
  };
}

function mapExcluded(raw: unknown): { employeeId: string; reason: ExcludedReason } {
  const r = obj(raw);
  return { employeeId: str(r.employeeId), reason: pick<ExcludedReason>(r.reason, 'NotActive') };
}

export function mapBulkPreview(raw: unknown): BulkAssignmentPreview {
  const r = obj(raw);
  const d = obj(r.deduction);
  const estimate = obj(r.estimatePerPayroll);
  const limits = obj(r.limits);
  return {
    deduction: {
      id: str(d.id),
      name: str(d.name),
      calculationType: pickOrNull<DeductionCalculationType>(d.calculationType),
      configurationStatus: pickOrNull<ConfigurationStatus>(d.configurationStatus),
      taxProfileCode: strOrNull(d.taxProfileCode),
      isActive: bool(d.isActive),
    },
    effectiveStartDate: str(r.effectiveStartDate),
    affectedCount: num(r.affectedCount),
    excluded: arr(r.excluded).map(mapExcluded),
    estimatePerPayroll: { amount: numOrNull(estimate.amount), amountPerEmployee: numOrNull(estimate.amountPerEmployee), basis: str(estimate.basis) },
    limits: { annualLimit: numOrNull(limits.annualLimit), perPayPeriodLimit: numOrNull(limits.perPayPeriodLimit), goalBalance: numOrNull(limits.goalBalance) },
    conflicts: strings(r.conflicts),
    errors: arr(r.errors).map((e) => ({ code: str(obj(e).code), message: str(obj(e).message) })),
    // Never assumed true: a response without the flag cannot be confirmed.
    canConfirm: bool(r.canConfirm, false),
  };
}

export function mapBulkResult(raw: unknown): BulkAssignmentResult {
  const r = obj(raw);
  return {
    deductionCodeId: str(r.deductionCodeId, str(obj(r.deduction).id)),
    effectiveStartDate: str(r.effectiveStartDate),
    // The API sends the ids of the elections it created (and `affectedCount`); an older shape sent `createdCount`.
    createdCount: Array.isArray(r.createdElectionIds) ? r.createdElectionIds.length : num(r.createdCount, num(r.affectedCount)),
    excluded: arr(r.excluded).map(mapExcluded),
    replayed: bool(r.replayed),
  };
}

const GARNISHMENT_TYPE_LABELS: Record<string, string> = {
  '1': 'Child support', ChildSupport: 'Child support',
  '2': 'Tax levy', TaxLevy: 'Tax levy',
  '3': 'Student loan default', StudentLoanDefault: 'Student loan default',
  '4': 'Creditor garnishment', CreditorGarnishment: 'Creditor garnishment',
  '5': 'Bankruptcy order', BankruptcyOrder: 'Bankruptcy order',
  '6': 'Other', Other: 'Other',
};
const GARNISHMENT_AMOUNT_LABELS: Record<string, string> = {
  '1': 'Flat amount', FlatAmount: 'Flat amount',
  '2': 'Percent of disposable earnings', PercentOfDisposableEarnings: 'Percent of disposable earnings',
};

/** The existing garnishment endpoints send numeric enums; a string enum is accepted too. */
export function mapGarnishment(raw: unknown): GarnishmentRecord {
  const r = obj(raw);
  return {
    id: str(r.id),
    employeeId: str(r.employeeId),
    caseNumber: str(r.caseNumber),
    typeLabel: GARNISHMENT_TYPE_LABELS[String(r.type)] ?? 'Other',
    priority: num(r.priority),
    amountTypeLabel: GARNISHMENT_AMOUNT_LABELS[String(r.amountType)] ?? 'Amount',
    amount: num(r.amount),
    maxPercentOfDisposableEarnings: numOrNull(r.maxPercentOfDisposableEarnings),
    issuingAuthority: strOrNull(r.issuingAuthority),
    effectiveStartDate: str(r.effectiveStartDate),
    effectiveEndDate: strOrNull(r.effectiveEndDate),
  };
}

// ---- the company catalog ----

export function mapCompanyCatalogItem(raw: unknown): CompanyCatalogItem {
  const r = obj(raw);
  const treatment = r.taxTreatment;
  return {
    key: str(r.key),
    itemType: pick<CatalogItemType>(r.itemType, 'Deduction'),
    id: str(r.id),
    name: str(r.name),
    shortCode: strOrNull(r.shortCode),
    categoryCode: str(r.categoryCode),
    categoryName: str(r.categoryName, str(r.categoryCode)),
    isCustom: bool(r.isCustom),
    isActive: bool(r.isActive, true),
    effectiveStartDate: strOrNull(r.effectiveStartDate),
    effectiveEndDate: strOrNull(r.effectiveEndDate),
    effectiveToday: bool(r.effectiveToday, true),
    calculationMethod: strOrNull(r.calculationMethod),
    frequency: strOrNull(r.frequency),
    defaultEmployeeAmount: numOrNull(r.defaultEmployeeAmount),
    defaultEmployeePercent: numOrNull(r.defaultEmployeePercent),
    defaultEmployerAmount: numOrNull(r.defaultEmployerAmount),
    defaultEmployerPercent: numOrNull(r.defaultEmployerPercent),
    coverageLevels: strings(r.coverageLevels) as CoverageLevel[],
    taxProfileCode: strOrNull(r.taxProfileCode),
    taxTreatment: treatment === 'PreTax' || treatment === 'PostTax' ? treatment : null,
    configurationStatus: str(r.configurationStatus, 'Complete'),
    assignable: bool(r.assignable),
    notAssignableCode: strOrNull(r.notAssignableCode),
    notAssignableReason: strOrNull(r.notAssignableReason),
    concurrencyVersion: num(r.concurrencyVersion, 1),
  };
}

export function mapCompanyCatalogPage(raw: unknown): CompanyCatalogPage {
  const paged = mapPaged(raw, mapCompanyCatalogItem);
  const sources = arr(obj(raw).sources).map((s) => ({
    itemType: pick<CatalogItemType>(obj(s).itemType, 'Deduction'),
    status: pick<CatalogSourceState>(obj(s).status, 'Unavailable'),
  }));
  return { ...paged, sources };
}
